/**
 * Share a running dev server (Run & Share, Phase 2): the REST calls and the
 * Tauri commands, kept in one file so tests mock one module.
 *
 * REST (authenticated /api/v1): the server checks the plan and the feature
 * switch when the tunnel is CREATED, so the app never decides alone that a
 * link may exist. Rust (`tunnel_client.rs`) then serves it on the user's
 * socket host; bodies never cross IPC.
 *
 * Everything heavy is imported lazily: the project page must not load the
 * Tauri APIs eagerly (tests/tauriImportsAreLazy).
 */

async function api() {
  const { apiClient } = await import('../../../../utils/apiClient');
  return apiClient;
}

async function invoke(cmd, args) {
  const core = await import('@tauri-apps/api/core');
  return core.invoke(cmd, args);
}

/** What a failed create means for the UI. */
export function createErrorKind(err) {
  const status = err?.response?.status;
  const code = err?.response?.data?.error || err?.response?.data?.code;
  if (status === 403 && (code === 'feature_disabled' || err?.response?.data?.reason === 'feature_disabled')) return 'feature_disabled';
  if (status === 403 && code === 'share_suspended') return 'share_suspended';
  if (status === 402 || code === 'plan_limit' || err?.response?.data?.upgrade_required) return 'plan_limit';
  return 'error';
}

/** POST /tunnels → { id, slug, public_url, expires_at }. */
export async function createTunnel(localLabel) {
  const client = await api();
  const res = await client.post('/tunnels', { local_label: localLabel });
  return res.data?.tunnel || res.data;
}

export async function deleteTunnel(id) {
  const client = await api();
  await client.delete(`/tunnels/${encodeURIComponent(id)}`);
}

export async function listTunnels() {
  const client = await api();
  const res = await client.get('/tunnels');
  return res.data?.tunnels || res.data || [];
}

/** GET /tunnels/usage → { used, cap, enabled, tunnelsLimit, suspended } (cap null = no cap). */
export async function tunnelUsage() {
  const client = await api();
  const res = await client.get('/tunnels/usage');
  return parseUsage(res.data);
}

export function parseUsage(data) {
  const d = data?.usage || data || {};
  const num = (...keys) => {
    for (const k of keys) {
      if (typeof d[k] === 'number' && Number.isFinite(d[k])) return d[k];
    }
    return null;
  };
  return {
    used: num('bytes_used', 'used_bytes', 'used') ?? 0,
    cap: num('bytes_limit', 'bytes_cap', 'cap_bytes', 'cap', 'limit_bytes'),
    // The gate (backend: the source of truth, it includes the server switch).
    enabled: typeof d.enabled === 'boolean' ? d.enabled : null,
    tunnelsLimit: num('tunnels_limit'),
    suspended: d.share_suspended === true,
  };
}

/** Ask the normal token refresh path for a fresh access token (Rust re-reads it on the next join). */
export async function refreshToken() {
  const { refreshNow } = await import('../../../../services/tokenRefresher');
  return refreshNow();
}

/** Fetch the plans catalog (for `limits.tunnels_limit` when /auth/usage lacks it). */
export async function fetchPlans() {
  const { getPlans } = await import('../../../../utils/apiClient');
  return getPlans();
}

export async function socketUrl() {
  const { getPhoenixSocketUrl } = await import('../../../../utils/phoenixSocketUrl');
  return getPhoenixSocketUrl();
}

export const openTunnel = (args) => invoke('tunnel_open', args);
export const closeTunnel = (args = {}) => invoke('tunnel_close', args);
export const tunnelStatus = () => invoke('tunnel_status');

/** Listen to a Rust event; resolves with an unlisten function. */
export async function listen(event, handler) {
  const { listenSafe } = await import('../../../../utils/safeTauriEvent');
  return listenSafe(event, handler);
}

export async function copyText(text) {
  const { copyTextToClipboard } = await import('../../../../utils/clipboard');
  return copyTextToClipboard(text);
}

/** Open the public link in the system browser (https only). */
export async function openLink(url) {
  if (!/^https:\/\//.test(String(url || ''))) return;
  const { openExternal } = await import('../../../../utils/openExternal');
  await openExternal(url);
}
